import { Link } from 'react-router-dom';
import { Shield } from 'lucide-react';
import LegalLayout from '../LegalLayout';

const PrivacyPolicy = () => {
  const magentaColor = '#d100a3';
  const lastUpdated = 'December 29, 2025';

  return (
    <LegalLayout app="ClipPilot" docType="Privacy Policy" icon={Shield} lastUpdated={lastUpdated}>
          <div className="space-y-8">
            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Introduction
              </h2>
              <p className="mb-4 leading-relaxed">
                Feral Techlab ("we," "our," or "us") operates the ClipPilot mobile application
                (the "App"). ClipPilot is a privacy-first clipboard manager designed to keep your
                data secure and under your control.
              </p>
              <p className="leading-relaxed">
                This Privacy Policy explains how we handle your information when you use our App.
                We are committed to protecting your privacy and being transparent about our practices.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Our Privacy Commitment
              </h2>
              <p className="mb-4 leading-relaxed">
                ClipPilot is built with privacy as a core principle:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong style={{ color: 'var(--text-primary)' }}>Zero Third-Party SDKs:</strong> We use only Apple's native frameworks. No analytics, tracking, or advertising SDKs are included.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>No User Accounts:</strong> The App does not require registration or login.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>No Analytics:</strong> We do not collect usage statistics or behavioral data.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>No Network Requests:</strong> By default, the App makes no network connections. Data stays on your device.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Information We Collect
              </h2>

              <h3 className="text-lg font-medium mb-3 mt-6" style={{ color: 'var(--text-primary)' }}>
                Clipboard Data
              </h3>
              <p className="mb-4 leading-relaxed">
                ClipPilot captures text content that you copy to your clipboard while the App is
                running in the foreground. This includes:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Text content you copy</li>
                <li>Automatically detected content type (OTP, URL, email, phone, address, code, or text)</li>
                <li>Timestamp of when the content was copied</li>
                <li>Source app identifier (optional)</li>
              </ul>
              <p className="leading-relaxed">
                <strong style={{ color: 'var(--text-primary)' }}>Important:</strong> All clipboard
                data is stored locally on your device and never transmitted to our servers.
              </p>

              <h3 className="text-lg font-medium mb-3 mt-6" style={{ color: 'var(--text-primary)' }}>
                Authentication Data
              </h3>
              <p className="leading-relaxed">
                If you set up a PIN, we store a securely hashed version (SHA-256 with a 32-byte salt)
                in your device's Keychain. We never store your actual PIN, and the hash cannot be
                reversed to reveal your PIN.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Data Storage & Security
              </h2>
              <p className="mb-4 leading-relaxed">
                Your data is protected by multiple security layers:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong style={{ color: 'var(--text-primary)' }}>Local Storage:</strong> All clipboard data is stored in an encrypted Core Data database on your device using iOS's built-in encryption.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>Biometric Protection:</strong> Access to the App can be protected with Face ID, Touch ID, or Optic ID.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>PIN Protection:</strong> A 4-8 digit PIN provides an additional layer of security with rate limiting (5 attempts before 5-minute lockout).</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>Auto-Lock:</strong> The App automatically locks when backgrounded.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>OTP Auto-Expiry:</strong> One-time passwords are automatically deleted after 10 minutes for enhanced security.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                iCloud Sync (Pro Feature)
              </h2>
              <p className="mb-4 leading-relaxed">
                If you purchase ClipPilot Pro and enable iCloud sync:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Your clipboard data is synced across your Apple devices using Apple's CloudKit.</li>
                <li>Data is stored in your private iCloud container, accessible only by you.</li>
                <li>All data is end-to-end encrypted by Apple.</li>
                <li>We cannot access, read, or decrypt your synced data.</li>
                <li>You can disable iCloud sync at any time in the App settings.</li>
              </ul>
              <p className="mt-4 leading-relaxed">
                For more information about iCloud security, visit{' '}
                <a href="https://support.apple.com/en-us/HT202303"
                   target="_blank"
                   rel="noopener noreferrer"
                   style={{ color: magentaColor }}>
                  Apple's iCloud Security Overview
                </a>.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Keyboard Extension
              </h2>
              <p className="mb-4 leading-relaxed">
                ClipPilot includes a keyboard extension for quick access to your clipboard history.
                Important information about the keyboard extension:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>The keyboard requires "Full Access" to function, which allows it to access the shared clipboard data.</li>
                <li>The keyboard extension <strong style={{ color: 'var(--text-primary)' }}>does not log keystrokes</strong>.</li>
                <li>It only accesses saved clipboard items from the main app via App Groups.</li>
                <li>The keyboard uses the same PIN/biometric protection as the main app.</li>
                <li>Keyboard unlock sessions expire after 120 seconds for security.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                What We Do NOT Collect
              </h2>
              <p className="mb-4 leading-relaxed">
                ClipPilot does not collect, store, or transmit:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Personal identification information</li>
                <li>Location data</li>
                <li>Contact information</li>
                <li>Photos or media files</li>
                <li>Browsing history</li>
                <li>Keystroke logs</li>
                <li>Device identifiers for tracking purposes</li>
                <li>Usage analytics or behavioral data</li>
                <li>Advertising data</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Third-Party Services
              </h2>
              <p className="leading-relaxed">
                ClipPilot uses <strong style={{ color: 'var(--text-primary)' }}>only Apple's native frameworks</strong>:
                SwiftUI, Core Data, CloudKit (optional), StoreKit, LocalAuthentication, CryptoKit,
                and Keychain Services. We do not integrate any third-party SDKs, analytics tools,
                crash reporters, or advertising networks.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Your Rights & Data Control
              </h2>
              <p className="mb-4 leading-relaxed">
                You have complete control over your data:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong style={{ color: 'var(--text-primary)' }}>View:</strong> Access all stored clipboard items within the App.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>Delete:</strong> Remove individual clips or clear all history at any time.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>Export:</strong> Copy any saved content back to your clipboard.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>Disable Sync:</strong> Turn off iCloud sync to keep data local only.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>Uninstall:</strong> Removing the App deletes all locally stored data.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Children's Privacy
              </h2>
              <p className="leading-relaxed">
                ClipPilot is not directed at children under the age of 13. We do not knowingly
                collect personal information from children. If you believe a child has provided
                us with personal information, please contact us so we can take appropriate action.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Changes to This Policy
              </h2>
              <p className="leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any
                changes by posting the new Privacy Policy on this page and updating the "Last
                updated" date. We encourage you to review this Privacy Policy periodically.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Contact Us
              </h2>
              <p className="leading-relaxed">
                If you have any questions about this Privacy Policy or our privacy practices,
                please contact us:
              </p>
              <div className="mt-4 space-y-2">
                <p>
                  <strong style={{ color: 'var(--text-primary)' }}>Email:</strong>{' '}
                  <a
                    href="mailto:support@feraltechlabs.co.in"
                    className="transition-colors"
                    style={{ color: magentaColor }}
                  >
                    support@feraltechlabs.co.in
                  </a>
                </p>
                <p>
                  <strong style={{ color: 'var(--text-primary)' }}>Support:</strong>{' '}
                  <Link
                    to="/apps/clippilot/support"
                    className="transition-colors"
                    style={{ color: magentaColor }}
                  >
                    ClipPilot Support Page
                  </Link>
                </p>
              </div>
            </section>
          </div>
    </LegalLayout>
  );
};

export default PrivacyPolicy;
